import React, { useEffect } from "react";
import CheckIcon from "@mui/icons-material/Check";
import CustomButton from "../../../../../components/buttons/CustomButton";
import useCranialEditor, { MAX_ZOOM, MIN_ZOOM } from "../../hooks/useCranialEditor";
import CalibrationPanel from "../CalibrationPanel";
import CranialCanvas from "./CranialCanvas";
import EditorToolbar from "./EditorToolbar";
import MeasurementPanel from "./MeasurementPanel";
import styles from "./CranialEditor.module.css";

const isTypingTarget = (el) =>
  el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable);

/**
 * Interactive cranial editor: toolbar + canvas on the left, live measurements and scale on
 * the right (stacked under the canvas on mobile). Geometry and calibration are controlled
 * by the parent (README 3.9); editor-only state lives in useCranialEditor.
 */
function CranialEditor({
  imageUrl,
  imageWidth,
  imageHeight,
  geometry,
  aiSuggestion,
  calibration,
  measurements,
  onGeometryChange,
  onCalibrationChange,
  onRestoreSuggestion,
  onConfirm,
}) {
  const editor = useCranialEditor({
    geometry,
    calibration,
    imageWidth,
    imageHeight,
    onGeometryChange,
    onCalibrationChange,
    onRestoreSuggestion,
  });
  const { mode, selectedPointIndex, undo, redo, removeContourPoint, selectPoint } = editor;

  // Keyboard shortcuts: Delete removes the selected point, Ctrl+Z / Ctrl+Y undo/redo.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isTypingTarget(e.target)) return;
      const ctrl = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      if (ctrl && key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (ctrl && (key === "y" || (key === "z" && e.shiftKey))) {
        e.preventDefault();
        redo();
      } else if ((e.key === "Delete" || e.key === "Backspace") && mode === "contour" && selectedPointIndex !== null) {
        e.preventDefault();
        removeContourPoint(selectedPointIndex);
      } else if (e.key === "Escape") {
        selectPoint(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [mode, selectedPointIndex, undo, redo, removeContourPoint, selectPoint]);

  const canRestore = Boolean(
    onRestoreSuggestion && aiSuggestion && Array.isArray(aiSuggestion.cranialContour) && geometry?.wasManuallyEdited
  );

  const handlePointTap = (target) => {
    if (target.kind === "contour") editor.selectPoint(target.index);
  };

  const handlePointDoubleTap = (target) => {
    if (target.kind === "contour") editor.removeContourPoint(target.index);
  };

  const handleBackgroundTap = (point) => {
    if (mode === "scale") editor.addScalePoint(point);
    else if (mode === "contour") editor.selectPoint(null);
  };

  const handleCalibrationPanelChange = (next, meta) => editor.updateCalibration(next, meta || {});

  return (
    <div className={styles.editor}>
      <div className={styles.canvasColumn}>
        <EditorToolbar
          mode={mode}
          onModeChange={editor.setMode}
          canUndo={editor.canUndo}
          canRedo={editor.canRedo}
          onUndo={undo}
          onRedo={redo}
          zoom={editor.zoom}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          onZoomIn={editor.zoomIn}
          onZoomOut={editor.zoomOut}
          onResetView={editor.resetView}
          canRestore={canRestore}
          onRestore={editor.restoreSuggestion}
          canRemovePoint={editor.canRemovePoint}
          onRemovePoint={() => removeContourPoint(selectedPointIndex)}
          canResetPosterior={editor.canResetPosterior}
          onResetPosterior={editor.resetPosterior}
        />
        <CranialCanvas
          imageUrl={imageUrl}
          imageWidth={imageWidth}
          imageHeight={imageHeight}
          geometry={geometry}
          calibration={calibration}
          measurements={measurements}
          aiSuggestion={aiSuggestion}
          mode={mode}
          selectedPointIndex={selectedPointIndex}
          zoom={editor.zoom}
          pan={editor.pan}
          onDragStart={editor.beginDrag}
          onDragMove={editor.dragTo}
          onDragEnd={editor.endDrag}
          onPointTap={handlePointTap}
          onPointDoubleTap={handlePointDoubleTap}
          onSegmentTap={editor.addContourPoint}
          onBackgroundTap={handleBackgroundTap}
          onZoom={editor.zoomBy}
          onPan={editor.panBy}
        />
      </div>

      <aside className={styles.sideColumn}>
        <MeasurementPanel measurements={measurements} />
        <CalibrationPanel
          calibration={calibration}
          imageWidth={imageWidth}
          imageHeight={imageHeight}
          onCalibrationChange={handleCalibrationPanelChange}
          onStartMarking={() => editor.setMode("scale")}
          isMarking={mode === "scale"}
        />
        <CustomButton
          rounded
          style="primary"
          label="Confirmar medidas"
          icon={<CheckIcon />}
          enabled={Boolean(measurements && measurements.ok)}
          onClick={() => onConfirm && onConfirm()}
          sx={{ width: "100%", minHeight: 48 }}
        />
      </aside>
    </div>
  );
}

export default CranialEditor;
